/**
 * Tiers as the editor holds them — `builder-engine-and-consistency.md` §6.8.
 *
 * The arithmetic is shared's (`tiersToBands`, `bandsToTiers`,
 * `tierCheckSizes`), re-exported so the engine has one import. A *draft* tier
 * is the same thing as typed: strings, because a half-typed "0.00" is somebody
 * on their way to 0.0015 and a number would eat the zeros.
 */
import { bandsToTiers, tiersToBands, type QuantityBand, type Tier } from '@rowted/shared'
import { formatNumber } from '../../lib/format.js'

export { bandsToTiers, tierCheckSizes, tiersToBands, type Tier } from '@rowted/shared'

/** 3000 → "3,000"; 0.0015 → "0.0015" — every digit a rate has, grouped like a measure. */
export function formatTierNumber(value: number): string {
  if (Number.isInteger(value)) return formatNumber(value)
  return String(Math.round(value * 1_000_000) / 1_000_000)
}

export interface DraftTier { upTo: string | null; qtyPerUnit: string }

/** "3,000" → 3000; anything unreadable is 0, which the editor shows as typed. */
export const readNumber = (text: string): number => {
  const value = Number(text.replace(/,/g, '').trim())
  return Number.isFinite(value) ? value : 0
}

export const toTiers = (draft: DraftTier[]): Tier[] => draft.map((tier) => ({
  upTo: tier.upTo === null ? null : readNumber(tier.upTo),
  qtyPerUnit: readNumber(tier.qtyPerUnit),
}))

/**
 * A line's stored bands as tiers to edit, or `null` when they are not stacked
 * (§6.8 item 5): those keep the old table until somebody presses Remove tiers.
 * A line with no bands has no tiers — the quiet row.
 */
export function draftTiersOf(
  line: { baseQty: number; qtyPerUnit: number; bands: QuantityBand[] },
): { tiers: DraftTier[] | null; stacked: boolean } {
  if (line.bands.length === 0) return { tiers: null, stacked: true }
  const tiers = bandsToTiers(line)
  if (!tiers) return { tiers: null, stacked: false }
  return {
    tiers: tiers.map((tier) => ({
      upTo: tier.upTo === null ? null : formatTierNumber(tier.upTo),
      qtyPerUnit: formatTierNumber(tier.qtyPerUnit),
    })),
    stacked: true,
  }
}

/**
 * The bands a recipe line's draft stores: tiers derived by `tiersToBands`,
 * bands typed by hand kept as typed (D6), else none. One function for the
 * service pop-out and the package builder, which edit the same line.
 */
export function bandsOfDraft(draft: {
  base: string; rate: string; tiers: DraftTier[] | null
  legacyBands: { minMeasure: string | number; baseQty: string | number; qtyPerUnit: string | number }[] | null
}): QuantityBand[] {
  const own = { baseQty: readNumber(draft.base), qtyPerUnit: readNumber(draft.rate) }
  if (draft.legacyBands) {
    return draft.legacyBands.map((band) => ({
      minMeasure: Number(band.minMeasure) || 0,
      baseQty: Number(band.baseQty) || 0,
      qtyPerUnit: Number(band.qtyPerUnit) || 0,
    }))
  }
  return draft.tiers && draft.tiers.length > 1 ? tiersToBands(own, toTiers(draft.tiers)) : []
}
